import { Injectable } from '@angular/core';
import { VendorsService } from '@buyiq-core/vendors/vendors.service';
import { VendorInfo } from '@buyiq-app/product/models/vendor-info';
import { VendorDiscountService } from '@buyiq-app/product/services/vendor-discount.service';
import { VendorInfoStorageService } from '@buyiq-app/product/services/vendor-info-storage.service';
import { VendorDiscountFilter } from '@buyiq-core/models/vendor-discount';
import { UserService } from '@buyiq-core/user/user.service';
import { SearchState } from '@cia-front-end-apps/shared/api-interaction';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { BatchService } from '@buyiq-app/batch/services/batch.service';
import { filterSuccessResult, takeUntilResultSuccess } from '@ngneat/query';
import { ChainStoreService } from '@buyiq-core/chain-store/chain-store.service';
import { DefaultOrderUnit, LegacyVendor } from '@buyiq-core/models/legacy-vendor';

@Injectable({
    providedIn: 'root',
})
export class VendorInfoService {
    private defaultOrderUnitCache = new Map<number, DefaultOrderUnit | null>();

    constructor(
        private userService: UserService,
        private batchService: BatchService,
        private newVendorsService: VendorsService,
        private vendorDiscountService: VendorDiscountService,
        private vendorInfoStorageService: VendorInfoStorageService,
        private chainStoreService: ChainStoreService
    ) {}

    preloadSupportedVendors(chainStoreId: number): Observable<Array<LegacyVendor>> {
        if (!chainStoreId) {
            return of([]);
        }

        return this.chainStoreService.getSupportedVendors(chainStoreId).pipe(
            tap((vendors) => this.persistSupportedVendors(vendors))
        );
    }

    /**
     * Returns the cached default order unit for a vendor.
     * Also triggers a background refresh to keep the cache up-to-date with changes
     * made in other applications. TanStack Query's stale time handles refresh throttling.
     */
    getCachedDefaultOrderUnit(legacyVendorId: number): DefaultOrderUnit | null {
        // Trigger background refresh (fire-and-forget)
        // TanStack Query cache will handle staleness and prevent excessive API calls
        this.newVendorsService
            .getVendorQuery(legacyVendorId)
            .result$.subscribe()
        // Return the cached value immediately for synchronous access
        return this.defaultOrderUnitCache.get(legacyVendorId) ?? null;
    }

    /**
     * Retrieves vendor information including partition settings, default order unit, and discounts.
     *
     * This method uses a two-phase approach:
     * 1. Phase 1: Load cached data from IndexedDB (for immediate fallback/offline support)
     * 2. Phase 2: Fetch fresh data from API via TanStack Query, falling back to cached data on error
     *
     * The cached data serves as resilience - if the API calls fail, we can still operate using
     * previously cached information. TanStack Query handles staleness and prevents excessive API calls.
     *
     * @param legacyVendorId The vendor ID to fetch information for
     * @returns Observable of VendorInfo containing partition settings, discounts, and default order unit
     */
    getVendorInfo(legacyVendorId: number): Observable<VendorInfo> {
        if (!legacyVendorId) {
            return of(
                new VendorInfo({
                    partitionEnabled: false,
                    discounts: [],
                })
            );
        }
        const searchState = new SearchState({
            pageSize: 25,
        });
        const filters = new VendorDiscountFilter({
            legacyVendorId,
            startDate: new Date(),
        });

        // Phase 1: Load cached vendor info from IndexedDB
        // This provides fallback values if API calls fail and updates the in-memory cache
        const cachedVendorInfo$ = this.vendorInfoStorageService
            .getItem(`${legacyVendorId}`)
            .pipe(
                tap((cached) => {
                    if (cached?.legacyVendorId) {
                        this.updateDefaultOrderUnitCache(
                            cached.legacyVendorId,
                            cached.defaultOrderUnit
                        );
                    }
                }),
                take(1)
            );

        // Phase 2: Fetch fresh data from API
        return cachedVendorInfo$.pipe(
            switchMap((cachedVendorInfo) =>
                combineLatest({
                    vendorMeta: this.newVendorsService
                        .getVendorQuery(legacyVendorId)
                        .result$.pipe(
                            filterSuccessResult(),
                            takeUntilResultSuccess(),
                            map((result) => result?.data),
                            map((vendor) => ({
                                partitionEnabled: vendor?.legacyVendor?.partitionEnabled ?? false,
                                defaultOrderUnit:
                                    vendor?.legacyVendor?.defaultOrderUnit ??
                                    cachedVendorInfo?.defaultOrderUnit ??
                                    null,
                            })),
                            catchError(() =>
                                of({
                                    partitionEnabled: cachedVendorInfo?.partitionEnabled ?? false,
                                    defaultOrderUnit: cachedVendorInfo?.defaultOrderUnit ?? null,
                                })
                            )
                        ),
                    discounts: this.userService.getCurrentUser().pipe(
                        switchMap((user) =>
                            this.vendorDiscountService
                                .getDiscountsQuery(
                                    user.currentStore.id,
                                    searchState,
                                    filters
                                )
                                .result$
                        ),
                        filterSuccessResult(),
                        takeUntilResultSuccess(),
                        map((discounts) => discounts?.data?.items ?? []),
                        catchError(() => of(cachedVendorInfo?.discounts ?? []))
                    ),
                }).pipe(
                    map(({ vendorMeta, discounts }) => {
                        const newVendorInfo = new VendorInfo({
                            legacyVendorId,
                            partitionEnabled: vendorMeta.partitionEnabled,
                            discounts,
                            defaultOrderUnit: vendorMeta.defaultOrderUnit,
                        });

                        return newVendorInfo;
                    }),
                    tap((vendorInfo) => {
                        this.updateDefaultOrderUnitCache(
                            vendorInfo.legacyVendorId,
                            vendorInfo.defaultOrderUnit
                        );
                        if (vendorInfo.discounts.length > 0) {
                            this.batchService.updateBatchNegotiatedPrices(
                                vendorInfo.discounts[0]
                            );
                        }
                        this.vendorInfoStorageService
                            .setItem(`${legacyVendorId}`, vendorInfo)
                            .pipe(take(1))
                            .subscribe();
                    }),
                    catchError(() =>
                        cachedVendorInfo
                            ? of(cachedVendorInfo)
                            : of(
                                  new VendorInfo({
                                      legacyVendorId,
                                      partitionEnabled: false,
                                      discounts: [],
                                  })
                              )
                    )
                )
            )
        );
    }

    isPartitionEnabled(legacyVendorId: number): Observable<boolean> {
        return this.vendorInfoStorageService.getItem(`${legacyVendorId}`).pipe(
            switchMap((localVendorInfo) => {
                if (localVendorInfo) {
                    return of(localVendorInfo.partitionEnabled);
                }

                return this.getVendorInfo(legacyVendorId).pipe(
                    switchMap((vendorInfo) => of(vendorInfo.partitionEnabled)),
                    take(1)
                );
            })
        );
    }

    private persistSupportedVendors(vendors: Array<LegacyVendor>): void {
        vendors?.forEach((vendor) => {
            const storageKey = `${vendor.id}`;
            this.vendorInfoStorageService
                .getItem(storageKey)
                .pipe(
                    take(1),
                    switchMap((existing) => {
                        const updatedVendorInfo = new VendorInfo({
                            legacyVendorId: vendor.id,
                            partitionEnabled: existing?.partitionEnabled ?? false,
                            discounts: existing?.discounts ?? [],
                            defaultOrderUnit:
                                vendor.defaultOrderUnit ?? existing?.defaultOrderUnit,
                        });

                        this.updateDefaultOrderUnitCache(
                            vendor.id,
                            updatedVendorInfo.defaultOrderUnit
                        );

                        return this.vendorInfoStorageService.setItem(
                            storageKey,
                            updatedVendorInfo
                        );
                    })
                )
                .subscribe();
        });
    }

    private updateDefaultOrderUnitCache(
        legacyVendorId: number,
        defaultOrderUnit?: DefaultOrderUnit
    ): void {
        if (legacyVendorId === null || legacyVendorId === undefined) {
            return;
        }

        this.defaultOrderUnitCache.set(
            legacyVendorId,
            defaultOrderUnit ?? null
        );
    }
}
